import moment from "moment";

export interface PmcAppointmentBulkUploadRow {
  rowNumber: number;
  property: string;
  property_unit: string;
  service: string;
  sub_service: string;
  vendor: string;
  date: string;
  work_notes?: string;
}

export interface VendorAppointmentBulkUploadRow {
  rowNumber: number;
  property: string;
  property_unit: string;
  service: string;
  sub_service: string;
  date: string;
  work_notes?: string;
}

export interface AppointmentBulkUploadParseResult<T> {
  isValid: boolean;
  errors: string[];
  data: T[];
}

interface ParseOptions {
  requiredHeaders: string[];
  optionalHeaders: string[];
  unexpectedHeadersSuffix?: string;
}

const FIELD_LABELS: Record<string, string> = {
  property: "Property",
  property_unit: "Property Unit",
  service: "Service",
  sub_service: "Sub Service",
  vendor: "Vendor",
  date: "Date",
};

const normalizeHeader = (header: string) => header.replace(/^\uFEFF/, "").trim().toLowerCase();

function parseAppointmentBulkTSV<T>(
  content: string,
  options: ParseOptions
): AppointmentBulkUploadParseResult<T> {
  if (!content.trim()) {
    return { isValid: false, errors: ["File is empty"], data: [] };
  }

  const lines = content.replace(/^\uFEFF/, "").split(/\r\n|\n|\r/);
  const errors: string[] = [];
  const data: T[] = [];
  const header = lines[0].split("\t").map(normalizeHeader);
  const acceptedHeaders = [...options.requiredHeaders, ...options.optionalHeaders];

  const missingHeaders = options.requiredHeaders.filter((h) => !header.includes(h));
  if (missingHeaders.length > 0) {
    errors.push(`Missing required headers: ${missingHeaders.join(", ")}`);
  }

  const unexpectedHeaders = header.filter((h) => h && !acceptedHeaders.includes(h));
  if (unexpectedHeaders.length > 0) {
    errors.push(`Unexpected headers: ${unexpectedHeaders.join(", ")}${options.unexpectedHeadersSuffix ?? ""}`);
  }

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values = line.split("\t");
    const row: Record<string, string> = {};

    header.forEach((h, index) => {
      if (!h) return;
      row[h] = values[index]?.trim() || "";
    });

    options.requiredHeaders.forEach((field) => {
      if (!row[field]) {
        errors.push(`Row ${i + 1}: ${FIELD_LABELS[field] ?? field} is required`);
      }
    });

    if (row.date) {
      const date = moment(row.date);
      if (!date.isValid()) {
        errors.push(`Row ${i + 1}: Invalid date format. Use YYYY-MM-DD or MM/DD/YYYY`);
      }
    }

    // Row numbers are 1-based file line numbers (header is row 1), matching the parse error messages.
    data.push({ ...row, rowNumber: i + 1 } as unknown as T);
  }

  return {
    isValid: errors.length === 0,
    errors,
    data,
  };
}

export function parsePmcAppointmentBulkTSV(
  content: string
): AppointmentBulkUploadParseResult<PmcAppointmentBulkUploadRow> {
  return parseAppointmentBulkTSV<PmcAppointmentBulkUploadRow>(content, {
    requiredHeaders: ["property", "property_unit", "service", "sub_service", "vendor", "date"],
    optionalHeaders: ["work_notes"],
  });
}

export function parseVendorAppointmentBulkTSV(
  content: string
): AppointmentBulkUploadParseResult<VendorAppointmentBulkUploadRow> {
  return parseAppointmentBulkTSV<VendorAppointmentBulkUploadRow>(content, {
    requiredHeaders: ["property", "property_unit", "service", "sub_service", "date"],
    optionalHeaders: ["work_notes"],
    unexpectedHeadersSuffix: ". Note: Vendor is automatically set to your organization.",
  });
}
